import { Entity } from "../../Entity";
import PaymentFactory from "../../Factories/PaymentFactory";
import { FactoryInterface } from "../../FactoryInterface";
import { PaymentInterface } from "./PaymentInterface";
import { PaymentTypeInterface } from "./PaymentType/PaymentTypeInterface";
import { PaymentClassInterface } from "./PaymentClass/PaymentClassInterface";
import { PaymentCreationTypeInterface } from "./PaymentCreationType/PaymentCreationTypeInterface";
import { CurrencyInterface } from "../../../Currency/CurrencyInterface";
import { PersonInterface } from "../Person/PersonInterface";
import { CompanyInterface } from "../Company/CompanyInterface";
import Translator from "../../../Translation/Translator";
import Store from "../../../Store/Store";
import PaymentTypeContainer from "./PaymentType/PaymentTypeContainer";
import PaymentClassContainer from "./PaymentClass/PaymentClassContainer";
import Formatter from "../../../Localization/Formatter";
import { ChargeInterface } from "../Charge/ChargeInterface";

class Payment extends Entity implements PaymentInterface {

  constructor(public data) {
    super();
  }

  public getFactory(): FactoryInterface {
    return PaymentFactory;
  }

  get nameOfEntity() {
    return Translator.trans("payment");
  }

  get nameForUrl() {
    return "payment";
  }

  get labelForEntity() {
    return "payment"
  }

  get displayName(): string {
    const layers: string[] = [];
    layers.push(this.getPayerDisplayName());
    layers.push(Formatter.formatNumber(this.amount, this.currency));
    layers.push(this.description);

    return layers.join(", ");
  }

  get listDisplayName(): string {
    return this.nameOfEntity + ", " + this.displayName;
  }

  get id(): number {
    return this.data.id;
  }

  get amount(): number {
    return this.data.amount;
  }

  get amountUsed(): number {
    return this.data.amountUsed;
  }

  public isFullyUsed(): boolean {
    return this.amount === this.amountUsed;
  }

  get amountRemaining(): number {
    return this.amount - this.amountUsed;
  }

  get parsedTransactionDescription() {
    return this.data.parsedTransactionDescription;
  }

  get description(): string {
    if (this.data.description) {
      return this.data.description;
    }

    if (this.hasTransaction) {
      switch (this.creationType.id) {
        case 2:
          return (this.transactionDetails?.description || "").replace(/\s+/g, ' ').trim();
        case 7:
          return this.generateDescriptionOfPontoPayment();
        default:
          return "";
      }
    }

    return "";
  }

  get reference(): string {
    return this.data.reference;
  }

  get firstDateOfStatementPeriod(): Date {
    return this.data.firstDateOfStatementPeriod;
  }

  get dateOfThePayment(): Date {
    return this.data.dateOfThePayment;
  }

  get currency(): CurrencyInterface {
    return this.data.currency;
  }

  get paymentType(): PaymentTypeInterface {
    return this.data.paymentType;
  }

  get paymentClass(): PaymentClassInterface {
    return this.data.paymentClass;
  }

  get creationType(): PaymentCreationTypeInterface {
    return this.data.creationType;
  }

  get creationTypeDisplayName(): string {
    if (this.creationType && this.creationType.id === 1) {
      return Translator.trans("creation-type-manual");
    }

    if (this.creationType && this.creationType.id === 3) {
      return Translator.trans("creation-type-auto");
    }

    return "";
  }

  public paymentListTypeLabel(): string {
    if (this.creationType?.id === 1) {
      return Translator.trans("creation-type-manual");
    }

    if (!this?.removed && this?.hasTransaction && !this?.isProcessed) {
      return Translator.trans("to-verify");
    }

    if (this.creationType?.id === 3) {
      return Translator.trans("creation-type-auto");
    }

    return "";
  }

  get isProcessed(): boolean {
    return this.data.isProcessed;
  }

  get ledgerAccountId(): string {
    return this.data.ledgerAccountId;
  }

  get bankTransactionId(): string {
    return this.data.bankTransactionId;
  }

  get personId(): number {
    return this.data.personId;
  }

  get person(): PersonInterface {
    return this.relation("app.persons", this.data.personId)[0];
  }

  get companyId(): number {
    return this.data.companyId;
  }

  get company(): CompanyInterface {
    return [...Store.getConfigurator().getState().app.companies]
            .find(company => company.id === this.data.companyId);
  }

  get amountDetails(): any {
    return this.data.amountDetails;
  }

  get transactionDetails(): any {
    return this.data.transactionDetails;
  }

  get chargeIds(): number[] {
    return this.data.chargeIds.map(key => parseInt(key, 10));
  }

  get linkedCharges(): ChargeInterface[] {
    return this.data.linkedCharges;
  }

  get relatedCharges(): ChargeInterface[] {
    return this.data.relatedCharges;
  }

  get createdAt(): Date {
    return this.data.createdAt;
  }

  get removed(): boolean {
    return this.data.removed;
  }

  get isLocked(): boolean {
    return this.data.isLocked;
  }

  public amountUsedForCharges(chargeIds: number[]): number {
    const linkedCharges = this.chargeIds.filter(chargeId => chargeIds.includes(chargeId));
    let sum = 0;

    linkedCharges.forEach(chargeId => {
      sum += this.amountDetails[chargeId];
    });

    return sum;
  }

  get hasTransaction(): boolean {
    return !!this.transactionDetails && Object.keys(this.transactionDetails || {}).length > 0;
  }

  get isSettle(): boolean {
    return this.data.isSettle;
  }

  get mainBankAccountId(): string {
    return this.data.mainBankAccountId;
  }

  get agencyBankAccount(): number {
    return this.data.agencyBankAccount;
  }

  get transactionFile(): object {
    return this.data.transactionFile;
  }

  get transactionFileId(): number {
    return this.data.transactionFileId;
  }

  public getPayerDisplayName(): string {
    if (!!this.name) {
      return this.name;
    }

    if (!!this.mainBankAccountId) {
      return this.mainBankAccountId;
    }

    return "";
  }

  public getIconColor(): string {
    if (this.amountUsed < this.amount) {
      if (this.amountUsed === 0) {
        return "#F44336";
      } else {
        return "#F5A623";
      }
    }

    return "#43A047";
  }

  get name(): string {
    return this.data.name;
  }

  public getStatusText(): string {
    if (this.isFullyUsed() === true) {
      return Translator.trans("status-used");
    }

    if (this.amountUsed === 0) {
      return Translator.trans("status-unused");
    }

    return Translator.trans("status-partially-used");
  }

  get isReceived(): boolean {
    return this.paymentClass.id === PaymentClassContainer.pickIdFromLabel("Receivable");
  }

  get isPaid(): boolean {
    return this.paymentClass.id === PaymentClassContainer.pickIdFromLabel("Payable");
  }

  get hasBankType(): boolean {
    return this.paymentType.id === PaymentTypeContainer.pickIdFromLabel("Bank");
  }

  get isSnoozed(): boolean {
    return this.data.isSnoozed;
  }

  get hasCashType(): boolean {
    return this.paymentType.id === PaymentTypeContainer.pickIdFromLabel("Cash");
  }

  get hasOtherType(): boolean {
    return this.paymentType.id === PaymentTypeContainer.pickIdFromLabel("Other");
  }

  get hasSettlementType(): boolean {
    return this.paymentType.id === PaymentTypeContainer.pickIdFromLabel("Settlement");
  }

  get hasSavingsType(): boolean {
    return this.paymentType.id === PaymentTypeContainer.pickIdFromLabel("Savings_account");
  }

  get note(): string {
    return this.data.note;
  }

  get isReferencedToSavings(): boolean {
    return this.data.isReferencedToSavings;
  }

  get isRefunded(): boolean {
    return this.data.isRefunded;
  }

  get hasMemos(): boolean {
    return this.data.hasMemos;
  }

  get ownerFinancialStatementId(): number {
    return this.data.ownerFinancialStatementId;
  }

  get isPaidDirectlyToOwner(): boolean {
    return this.data.isPaidDirectlyToOwner;
  }

  get counterparty(): CompanyInterface | PersonInterface {
    if (!!this.person) {
      return this.person;
    }

    if (!!this.company) {
      return this.company;
    }
  }

  get isClosedInStatement(): boolean {
    return this.data.isClosedInStatement;
  }

  get isReadonly(): boolean {
    return this.hasSavingsType ||
           this.isReferencedToSavings ||
           this.isPaidDirectlyToOwner ||
           this.hasSettlementType ||
           this.isSettle ||
           this.isRefunded ||
           this.hasTransaction ||
           this.removed;
  }

  public getPaymentAmountHint(): string {
    let status = "";

    if ((0 < this.amountRemaining && this.amountRemaining < this.amount) && this.isPaid){
      status = `${Formatter.formatNumber(this.amountRemaining, this.currency)} ${Translator.trans("payment-status-still-to-pay")}`
    } else if ((0 < this.amountRemaining && this.amountRemaining < this.amount) && this.isReceived){
      status = `${Formatter.formatNumber(this.amountRemaining, this.currency)} ${Translator.trans("payment-status-still-to-receive")}`
    } else if (this.amount > 0 && this.amountRemaining === this.amount && this.isPaid){
      status = `${Translator.trans("payment-status-to-pay")}`
    } else if (this.amount > 0 && this.amountRemaining === this.amount && this.isReceived){
      status = `${Translator.trans("payment-status-to-receive")}`
    } else if(this.isFullyUsed() && this.isReceived){
      status = `${Translator.trans("status-received")}`

      if (this.hasSavingsType) {
        status = `${Translator.trans("status-withdrawal")}`
      }
    } else if(this.isFullyUsed() && this.isPaid){
      status = `${Translator.trans("status-amount/paid")}`

      if (this.hasSavingsType) {
        status = `${Translator.trans("status-deposit")}`
      }
    }

    return status;
  }

  public generateDescriptionOfPontoPayment(): string {
    return Object.entries(this.parsedTransactionDescription).reduce((str, [p, val]) => {
      return `${str}${p}: ${val} `;
    }, '');
  }
}

export { Payment };
